import { AxiosResponse } from 'axios'
import { NavigateFunction } from 'react-router-dom'
import { urlList } from '../constants/urlConstant'
import {
  AlgorithmRun,
  AlgorithmRunConfig,
  AlgorithmRunResult,
  NotifyUsersOfSmartFunctionResultsBody,
  SmartFunctionRunStatus,
} from '../store/reducers/workspaceReducer'
import apiService from './apiService'

const baseUrl = urlList.WORKSPACES

interface RunAlgorithmProps {
  id: string
  navigate?: NavigateFunction
  config?: AlgorithmRunConfig
}

interface CancelRunProps {
  id: string
  algorithmRunId: string
  navigate: NavigateFunction
}

const runAlgorithm = async ({ id, navigate, config }: RunAlgorithmProps) => {
  return apiService
    .saveItem(`${baseUrl}/${id}/algorithmRuns`, navigate, {
      config: config,
    })
    .then((response) => {
      return response
    })
    .catch((error) => {
      console.error('axios create error', error)
      throw error
    })
}

const cancelRun = async ({ id, algorithmRunId, navigate }: CancelRunProps) => {
  return apiService
    .deleteItem(
      `${baseUrl}/${id}/algorithmRuns/${algorithmRunId}/cancel`,
      navigate
    )
    .then((response) => {
      return response
    })
    .catch((error) => {
      console.error('axios create error', error)
      throw error
    })
}

interface getWorkspaceAlgorithmRunProps {
  workspaceId: string
  algorithmRunId: string
}

const getWorkspaceAlgorithmRun = async ({
  workspaceId,
  algorithmRunId,
}: getWorkspaceAlgorithmRunProps) => {
  return apiService
    .fetchItems(`${baseUrl}/${workspaceId}/algorithmRuns/${algorithmRunId}`)
    .then((response: AlgorithmRun) => {
      return response
    })
    .catch((error) => {
      console.error('axios get error', error)
      throw error
    })
}

interface ExportRunProps {
  workspaceId: string
  algorithmRunId: string
}

const exportRun = async ({ workspaceId, algorithmRunId }: ExportRunProps) => {
  return apiService
    .fetchItems(
      `${baseUrl}/${workspaceId}/algorithmRuns/${algorithmRunId}/result/csv`
    )
    .then((response) => {
      return response
    })
    .catch((error) => {
      console.error('axios get error', error)
      throw error
    })
}

const getSmartFunctionRunResults = async ({
  workspaceId,
  algorithmRunId,
}: {
  workspaceId: string
  algorithmRunId: string
}): Promise<AlgorithmRunResult> => {
  return apiService
    .fetchItems(
      `${baseUrl}/${workspaceId}/algorithmRuns/${algorithmRunId}/results`
    )
    .then((response) => {
      return response as AlgorithmRunResult
    })
    .catch((error) => {
      console.error('Error fetching smart function run results:', error)
      throw error
    })
}

const getSmartFunctionRunStatus = async ({
  workspaceId,
  algorithmRunId,
}: {
  workspaceId: string
  algorithmRunId: string
}): Promise<SmartFunctionRunStatus> => {
  return apiService
    .fetchItems(
      `${baseUrl}/${workspaceId}/algorithmRuns/${algorithmRunId}/statuses`
    )
    .then((response) => {
      return response as SmartFunctionRunStatus
    })
    .catch((error) => {
      console.error('Error fetching smart function run status:', error)
      throw error
    })
}

interface GetSmartFunctionRunWorkflowNodeProps {
  nodeId: string
  algorithmRunId: string
  workspaceId: string
}

const getSmartFunctionRunWorkflowNode = async ({
  nodeId,
  algorithmRunId,
  workspaceId,
}: GetSmartFunctionRunWorkflowNodeProps): Promise<any> => {
  return apiService
    .fetchItems(
      `${baseUrl}/${workspaceId}/algorithmRuns/${algorithmRunId}/workflowNodes/${nodeId}`
    )
    .then((response) => {
      return response
    })
    .catch((error) => {
      console.error('Error fetching smart function run workflow nodes:', error)
      throw error
    })
}

interface ResumeSmartFunctionWorkflowProps {
  workspaceId: string
  algorithmRunId: string
  config: AlgorithmRunConfig
  navigate: NavigateFunction
}

const resumeSmartFunctionWorkflow = async ({
  workspaceId,
  algorithmRunId,
  config,
  navigate,
}: ResumeSmartFunctionWorkflowProps) => {
  return apiService
    .saveItem(
      `${baseUrl}/${workspaceId}/algorithmRuns/${algorithmRunId}/resume`,
      navigate,
      {
        config,
      }
    )
    .then((response) => {
      return response
    })
    .catch((error) => {
      console.error('Error resuming smart function workflow:', error)
      throw error
    })
}

interface ResumeSmartFunctionNodeProps {
  workspaceId: string
  runId: string
  nodeId: string
  navigate: NavigateFunction
  config: AlgorithmRunConfig
}

const resumeSmartFunctionNode = async ({
  workspaceId,
  runId,
  nodeId,
  navigate,
  config,
}: ResumeSmartFunctionNodeProps): Promise<AxiosResponse<AlgorithmRun>> => {
  return apiService
    .saveItem(
      `${baseUrl}/${workspaceId}/algorithmRuns/${runId}/nodeId=${nodeId}/resume`,
      navigate,
      {
        config,
      }
    )
    .then((response) => {
      return response
    })
    .catch((error) => {
      console.error('Error resuming smart function workflow:', error)
      throw error
    })
}

interface NotifyUsersOfSmartFunctionResults {
  workspaceId: string
  algorithmRunId: string
  navigate: NavigateFunction
  body: NotifyUsersOfSmartFunctionResultsBody
}

const notifyUsersOfSmartFunctionResults = async ({
  workspaceId,
  algorithmRunId,
  body,
  navigate,
}: NotifyUsersOfSmartFunctionResults) => {
  return apiService
    .saveItem(
      `${baseUrl}/${workspaceId}/algorithmRuns/${algorithmRunId}/notify`,
      navigate,
      body
    )
    .then((response) => {
      return response
    })
    .catch((error) => {
      console.error('axios notify error', error)
      throw error
    })
}

const smartFunctionService = {
  runAlgorithm,
  cancelRun,
  exportRun,
  getWorkspaceAlgorithmRun,
  getSmartFunctionRunStatus,
  getSmartFunctionRunResults,
  getSmartFunctionRunWorkflowNode,
  resumeSmartFunctionWorkflow,
  resumeSmartFunctionNode,
  notifyUsersOfSmartFunctionResults,
}

export default smartFunctionService
